import { EquipmentService } from '../../../services/equipment.service';
import { EquipmentType } from '../../../enums/equipment-type';
import * as yup from 'yup';
import { TFunction } from 'i18next';
import {
  AllEquipmentFormFields,
  MachineAndAggregateFields,
  SelfPropelledMachineryFields,
} from '../../../interfaces/equipment';
import { ClassifierService } from '../../../services/classifier.service';
import * as H from 'history';
import Watch from '../../../components/form/elements/watch';
import Select from '../../../components/form/elements/select';
import {
  getEquipmentMeasurementOptionsForType,
  getEquipmentTypeValues,
} from './hardcoded';
import { route } from '../../../services/utils';
import { EQUIPMENT_LIST_CREATE } from '../../../router/routes';
import { map } from 'rxjs/operators';
import Paginate from '../../../interfaces/paginate';
import ClassifierValue from '../../../interfaces/classifierValue';
import Text from '../../../components/form/elements/text';
import Checkbox from '../../../components/form/elements/checkbox';
import TextArea from '../../../components/form/elements/text-area';
import Group from '../../../components/form/elements/group';
import { FormMode } from '../../../enums/form-mode';
import Readonly from '../../../components/form/elements/readonly';
import Hidden from '../../../components/form/elements/hidden';
import Integer from '../../../components/form/elements/integer';
import Decimal from '../../../components/form/elements/decimal';

export function getEquipmentSchema(
  equipmentService: EquipmentService,
  equipmentType: string,
  needsWidthAndMeasurement: boolean
) {
  const isSelfPropelledMachinery =
    equipmentType === EquipmentType.selfPropelledMachinery;
  const validations = yup.object().shape({
    equipment_type: yup.string().max(255).required(),
    type_id: yup.object().shape({
      value: yup.string().uuid().required(),
    }),
    make: isSelfPropelledMachinery
      ? yup.string().max(255).required()
      : yup.string().max(255).nullable(),
    model: yup.string().max(255).nullable(),
    active: yup.boolean().required(),
    notes: yup.string().nullable(),
    registration_number: yup.string().max(255).nullable(),
    registration_year: yup.number().integer().nullable(),
    horsepower: isSelfPropelledMachinery
      ? yup.number().integer().required()
      : yup.number().integer().nullable(),
    width: needsWidthAndMeasurement
      ? yup.number().positive().decimal().required()
      : yup.number().positive().decimal().nullable(),
    measurement: needsWidthAndMeasurement
      ? yup.string().required()
      : yup.string().nullable(),
  });
  return validations;
}

export function getEquipmentFormBodyElements(
  formMode: FormMode,
  farmId: string,
  t: TFunction,
  equipmentType: EquipmentType,
  needsWidthAndMeasurement: boolean,
  formValues: SelfPropelledMachineryFields | MachineAndAggregateFields,
  setFormValues: Function,
  setNeedsWidthAndMeasurement: Function,
  classifierService: ClassifierService,
  equipmentService: EquipmentService,
  history: H.History
) {
  let baseFields = [
    // tips - equipment_type
    formMode === FormMode.edit // we do not allow changing this in edit mode
      ? Readonly(Text('equipment_type_text', 'pages.equipment.equipment_type'))
      : Watch(
          Select('equipment_type', {
            options: getEquipmentTypeValues(t),
            label: t('pages.equipment.equipment_type'),
          }),
          ({ value, setValue }) => {
            if (value && value !== formValues.equipment_type) {
              // here we update the value, to refresh fields/validations (different equipment types need different behaviour)
              setFormValues({
                ...formValues,
                equipment_type: value,
                type_id: null,
                type_value: null,
                type: null,
              });
              // we also update the quipment type value in the URL, since it's possible to open it from either list tab
              const newLocation = route(EQUIPMENT_LIST_CREATE, {
                farmId: farmId,
                equipmentType: formValues.equipment_type,
              });
              history.push(newLocation);
              // we update the form values as well as needed
              setValue('type_id', null);
              setValue('type_value', null);
              setValue('type', null);
            }
          }
        ),
    // veids - type_id
    Watch(
      Select('type_id', {
        asyncOptions: (value, loadedOptions, { page }) => {
          const params: any = { page, value };
          return classifierService
            .valuesPaged('equipment', formValues.equipment_type, params)
            .pipe(
              map((response: Paginate<ClassifierValue>) => ({
                hasMore: response.current_page !== response.last_page,
                options: response.data.map((v) => ({
                  label: v.value,
                  value: v.id,
                  data: v,
                })),
                additional: { page: response.current_page + 1 },
              }))
            );
        },
        label: 'pages.equipment.type',
        cacheOn: ['equipment_type'],
      }),
      ({ value, setValue }) => {
        // here we update the value explicitly, to refresh fields/validations (different types need different behaviour)
        if (value && value !== formValues.type_id) {
          setFormValues({
            ...formValues,
            type_id: value,
            type_value: value.value,
            type: value.data, // the classifier properties will be used for updating required fields
          });
          // we update the form values as well as needed
          setValue('type_id', value);
          setValue('type_value', value.value);
          setValue('type', value.data);
        }
      }
    ),
    // marka - make
    Text('make', 'pages.equipment.make'),
    // modelis - model
    Text('model', 'pages.equipment.model'),
  ];
  // when we are in edit mode, we don't allow editing equipment type, however the schema still requires that we have the actual form field
  if (formMode === FormMode.edit) {
    baseFields.push(Hidden('equipment_type'));
  }
  let selfPropelledMachineryFields = [
    // reģistrācijas nr. - registration_number
    Text('registration_number', 'pages.equipment.registration_number'),
    // reģistrācijas gads - registration_year
    Integer('registration_year', 'pages.equipment.registration_year'),
    // jauda, zs - horsepower
    Integer('horsepower', 'pages.equipment.horsepower'),
  ];
  let widthAndMeasurementFields = [
    // platums - width
    Decimal('width', 'pages.equipment.width'),
    Select('measurement', {
      options: getEquipmentMeasurementOptionsForType(equipmentType),
      label: t('pages.equipment.measurement'),
    }),
  ];
  let baseFieldsEnd = [
    // aktuāls - active
    Checkbox('active', t('pages.equipment.active')),
    // piezīmes - notes
    TextArea('notes', t('pages.equipment.notes')),
  ];
  switch (formValues.equipment_type) {
    case EquipmentType.machinesAndAggregates:
      return needsWidthAndMeasurement
        ? [
            Group(
              baseFields.concat(widthAndMeasurementFields).concat(baseFieldsEnd)
            ),
          ]
        : [Group(baseFields.concat(baseFieldsEnd))];
    case EquipmentType.selfPropelledMachinery:
      return needsWidthAndMeasurement
        ? [
            Group(
              baseFields
                .concat(selfPropelledMachineryFields)
                .concat(widthAndMeasurementFields)
                .concat(baseFieldsEnd)
            ),
          ]
        : [
            Group(
              baseFields
                .concat(selfPropelledMachineryFields)
                .concat(baseFieldsEnd)
            ),
          ];
    default:
      return [Group([])];
  }
}

export function getEquipmentTypeText(
  t: TFunction,
  equipmentType: string
): string {
  const translationKey = 'pages.equipment.' + equipmentType + '.title';
  return t(translationKey);
}

// much like the above, however changing of values won't cause data to be cleared, since submitting isn't a thing here
// also uses text fields for some of the Select fields, since we already get these values from the back end
export function getEquipmentFormBodyElementsView(
  farmId: string,
  t: TFunction,
  equipmentType: EquipmentType,
  needsWidthAndMeasurement: boolean,
  formValues: SelfPropelledMachineryFields | MachineAndAggregateFields,
  setFormValues: Function,
  setNeedsWidthAndMeasurement: Function,
  classifierService: ClassifierService,
  equipmentService: EquipmentService,
  history: H.History
) {
  let baseFields = [
    // tips - equipment_type (but here we show the text value)
    Text('equipment_type_text', 'pages.equipment.equipment_type'),
    // veids - type_id (but here we show the type value directly)
    Text('type_value', 'pages.equipment.type'),
    // marka - make
    Text('make', 'pages.equipment.make'),
    // modelis - model
    Text('model', 'pages.equipment.model'),
  ];
  let selfPropelledMachineryFields = [
    // reģistrācijas nr. - registration_number
    Text('registration_number', 'pages.equipment.registration_number'),
    // reģistrācijas gads - registration_year
    Integer('registration_year', 'pages.equipment.registration_year'),
    // jauda, zs - horsepower
    Integer('horsepower', 'pages.equipment.horsepower'),
  ];
  let widthAndMeasurementFields = [
    // platums - width
    Integer('width', 'pages.equipment.width'),
    Select('measurement', {
      options: getEquipmentMeasurementOptionsForType(equipmentType),
      label: t('pages.equipment.measurement'),
    }),
  ];
  let baseFieldsEnd = [
    // aktuāls - active
    Checkbox('active', t('pages.equipment.active')),
    // piezīmes - notes
    TextArea('notes', t('pages.equipment.notes')),
  ];
  switch (formValues.equipment_type) {
    case EquipmentType.machinesAndAggregates:
      return needsWidthAndMeasurement
        ? [
            Group(
              baseFields.concat(widthAndMeasurementFields).concat(baseFieldsEnd)
            ),
          ]
        : [Group(baseFields.concat(baseFieldsEnd))];
    case EquipmentType.selfPropelledMachinery:
      return needsWidthAndMeasurement
        ? [
            Group(
              baseFields
                .concat(selfPropelledMachineryFields)
                .concat(widthAndMeasurementFields)
                .concat(baseFieldsEnd)
            ),
          ]
        : [
            Group(
              baseFields
                .concat(selfPropelledMachineryFields)
                .concat(baseFieldsEnd)
            ),
          ];
    default:
      return [Group([])];
  }
}

export function createNewFormValues(
  equipmentType: string
): AllEquipmentFormFields {
  return {
    id: null, // generated server-side, but required by the schema
    farm_id: null, // generated server-side, but required by the schema
    equipment_type: equipmentType,
    type_id: null,
    type_value: null, // only for reading
    type: null,
    make: null,
    model: null,
    label: null,
    registration_number: null,
    registration_year: null,
    horsepower: null,
    width: null,
    measurement: null,
    active: true,
    notes: null,
  };
}

/**
 * The current form logic doesn't remove values for fields which are not shown (i.e. after switching type and the form fields being different).
 * This helper allows cleaning up those values before submit, based on the business rules.
 */
export function prepareFormForSubmit(
  needsWidthAndMeasurement: boolean,
  form: SelfPropelledMachineryFields & MachineAndAggregateFields
): SelfPropelledMachineryFields & MachineAndAggregateFields {
  let preparedForm: SelfPropelledMachineryFields & MachineAndAggregateFields =
    null;
  switch (form.equipment_type) {
    case EquipmentType.machinesAndAggregates:
      preparedForm = {
        ...form,
        // clean fields that belong to self propelled machinery
        registration_number: null,
        registration_year: null,
        horsepower: null,
        // make sure width and measurement fields actually required
        width: needsWidthAndMeasurement ? form.width : null,
        measurement: needsWidthAndMeasurement ? form.measurement : null,
      };
      break;
    case EquipmentType.selfPropelledMachinery:
      preparedForm = {
        ...form,
        // make sure width and measurement fields actually required
        width: needsWidthAndMeasurement ? form.width : null,
        measurement: needsWidthAndMeasurement ? form.measurement : null,
      };
      break;
    default:
      preparedForm = { ...form }; // no transformations
      break;
  }
  return preparedForm;
}
